// ============================================================
// ReplayPage — The core product experience
// Desktop: board (60%) + narration panel (40%) side by side
// Mobile: board sticky at top, narration scrolls below
// ============================================================

import { useEffect, useCallback, useState } from "react"
import { useParams } from "react-router"
import {
  useReplayStore,
  getCurrentFen,
  getCurrentEval,
  getLastMove,
  getCurrentPhase,
  getCurrentCallout,
  getCurrentMoveCommentary,
  getBoardHighlights,
  getMaterialScore,
} from "../stores/replay-store"
import { ChessBoard } from "../components/ChessBoard"
import { EvalBar } from "../components/EvalBar"
import { ProgressBar } from "../components/ProgressBar"
import { NavControls } from "../components/NavControls"
import { NarrationPanel } from "../components/NarrationPanel"
import { MoveList } from "../components/MoveList"
import { Toast } from "../components/Toast"
import { useKeyboardControls } from "../hooks/useKeyboardControls"
import { MOCK_REPLAY } from "../data/mock-replay"
import type { ReplayData } from "@shared/types"
import styles from "./ReplayPage.module.css"

export const ReplayPage = () => {
  const { id } = useParams<{ id: string }>()

  // Store state
  const replayData = useReplayStore((s) => s.replayData)
  const mode = useReplayStore((s) => s.mode)
  const status = useReplayStore((s) => s.status)
  const currentMoveIndex = useReplayStore((s) => s.currentMoveIndex)
  const totalMoves = useReplayStore((s) => s.totalMoves)
  const isAutoPlaying = useReplayStore((s) => s.isAutoPlaying)
  const boardOrientation = useReplayStore((s) => s.boardOrientation)

  // Derived state
  const fen = useReplayStore(getCurrentFen)
  const evalCp = useReplayStore(getCurrentEval)
  const lastMove = useReplayStore(getLastMove)
  const currentPhase = useReplayStore(getCurrentPhase)
  const currentCallout = useReplayStore(getCurrentCallout)
  const currentMoveCommentary = useReplayStore(getCurrentMoveCommentary)
  const highlights = useReplayStore(getBoardHighlights)
  const material = useReplayStore(getMaterialScore)

  // Actions
  const initReplay = useReplayStore((s) => s.initReplay)
  const startReplay = useReplayStore((s) => s.startReplay)
  const stepForward = useReplayStore((s) => s.stepForward)
  const stepBackward = useReplayStore((s) => s.stepBackward)
  const goToMove = useReplayStore((s) => s.goToMove)
  const jumpToNextSegment = useReplayStore((s) => s.jumpToNextSegment)
  const jumpToPreviousSegment = useReplayStore((s) => s.jumpToPreviousSegment)
  const toggleAutoPlay = useReplayStore((s) => s.toggleAutoPlay)
  const flipBoard = useReplayStore((s) => s.flipBoard)
  const switchToExplore = useReplayStore((s) => s.switchToExplore)
  const switchToGuided = useReplayStore((s) => s.switchToGuided)
  const reset = useReplayStore((s) => s.reset)

  // Initialize replay data
  useEffect(() => {
    // Check for embedded data first (from Worker server-render)
    const windowData = (window as unknown as { __REPLAY_DATA__?: ReplayData }).__REPLAY_DATA__
    if (windowData) {
      initReplay(windowData)
      return
    }

    // Fallback to mock data for development
    if (id === "mock" || id === "opera123" || !id) {
      initReplay(MOCK_REPLAY)
      return
    }

    // Fetch replay data from API
    let cancelled = false
    fetch(`/api/replay/${id}`)
      .then((res) => {
        if (!res.ok) throw new Error("Replay not found")
        return res.json()
      })
      .then((data) => {
        if (!cancelled) initReplay(data as ReplayData)
      })
      .catch((err) => {
        console.error("Failed to load replay:", err)
        // Could show an error state here
      })

    return () => { cancelled = true }
  }, [id, initReplay])

  // Keyboard controls
  useKeyboardControls({
    onStepForward: stepForward,
    onStepBackward: stepBackward,
    onJumpNextSegment: jumpToNextSegment,
    onJumpPreviousSegment: jumpToPreviousSegment,
    onToggleAutoPlay: toggleAutoPlay,
    onFlipBoard: flipBoard,
    onStart: status === "intro" ? startReplay : undefined,
    enabled: !!replayData,
  })

  // Toast state
  const [toastMessage, setToastMessage] = useState<string | null>(null)

  // Copy link handler
  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setToastMessage("Link copied to clipboard")
    } catch {
      setToastMessage("Could not copy — try manually")
    }
  }, [])

  // Check if we're at a critical moment (for eval bar animation)
  const isCriticalMoment = currentCallout !== null

  // Detect template fallback (AI narration was unavailable)
  const isTemplateFallback = replayData
    ? replayData.narration.phases.every((p) => p.source === "template")
    : false

  // Don't render until data is loaded
  if (!replayData) {
    return (
      <main className={styles.container}>
        <div className={styles.loading}>Loading replay...</div>
      </main>
    )
  }

  const canStepBack = currentMoveIndex > -1 && status !== "intro"
  const canStepForward = currentMoveIndex < totalMoves - 1 && status !== "intro"

  return (
    <main className={styles.container}>
      {/* Mobile: headline above board */}
      {status === "intro" && (
        <div className={styles.mobileHeadline}>
          <h1 className={styles.mobileTitle}>
            {replayData.narration.storyHeadline}
          </h1>
          <p className={styles.mobileSubtitle}>
            {replayData.metadata.white} vs. {replayData.metadata.black}
          </p>
        </div>
      )}

      <div className={styles.replayLayout}>
        {/* Left column: eval bar + board + progress + nav */}
        <div className={styles.boardColumn}>
          {/* Mobile eval bar (horizontal, above board) */}
          <div className={styles.evalBarMobile}>
            <EvalBar
              eval_cp={evalCp}
              isCritical={isCriticalMoment}
              orientation="horizontal"
            />
          </div>

          <div className={styles.boardArea}>
            {/* Desktop eval bar (vertical, left of board) */}
            <div className={styles.evalBarDesktop}>
              <EvalBar
                eval_cp={evalCp}
                isCritical={isCriticalMoment}
                orientation="vertical"
              />
            </div>

            {/* Chess board */}
            <div className={`${styles.boardContainer} ${status === "ended" ? styles.boardEnded : ""}`}>
              <ChessBoard
                fen={fen}
                orientation={boardOrientation}
                lastMove={lastMove}
                highlights={status !== "intro" ? highlights : []}
                onSwipeLeft={stepForward}
                onSwipeRight={stepBackward}
              />
            </div>
          </div>

          {/* Material score (hidden during intro) */}
          {status !== "intro" && (
            <div className={styles.materialScore}>
              {material.diff !== 0 && (
                <span className={styles.materialDiff}>
                  {material.diff > 0
                    ? `${replayData.metadata.white.split(" (")[0]} +${material.diff}`
                    : `${replayData.metadata.black.split(" (")[0]} +${Math.abs(material.diff)}`
                  }
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right column: narration + controls */}
        <div className={styles.narrationColumn}>
          {isTemplateFallback && (
            <div className={styles.fallbackBanner} role="status">
              AI narration was unavailable for this replay. You're seeing auto-generated commentary.
            </div>
          )}

          {mode === "explore" ? (
            <div className={styles.explorePanel}>
              <div className={styles.exploreHeader}>
                <h2 className={styles.exploreTitle}>Free Explore</h2>
                <button
                  className={styles.modeToggle}
                  onClick={switchToGuided}
                >
                  Back to guided story
                </button>
              </div>
              <MoveList
                moves={replayData.moves}
                currentMoveIndex={currentMoveIndex}
                onGoToMove={goToMove}
              />
            </div>
          ) : (
            <NarrationPanel
              narration={replayData.narration}
              metadata={replayData.metadata}
              status={status}
              mode={mode}
              currentPhase={currentPhase}
              currentCallout={currentCallout}
              currentMoveCommentary={currentMoveCommentary}
              currentMoveIndex={currentMoveIndex}
              totalMoves={totalMoves}
              onStart={startReplay}
              onExplore={switchToExplore}
              onGuided={switchToGuided}
              onReset={reset}
              onCopyLink={handleCopyLink}
            />
          )}

          {/* Controls under narration (hidden during intro) */}
          {status !== "intro" && (
            <div className={styles.controls}>
              <ProgressBar
                currentMoveIndex={currentMoveIndex}
                totalMoves={totalMoves}
                moves={replayData.moves}
                phases={replayData.narration.phases}
                momentCallouts={replayData.narration.momentCallouts}
                onSeek={goToMove}
              />
              <NavControls
                onStepBack={stepBackward}
                onStepForward={stepForward}
                onToggleAutoPlay={toggleAutoPlay}
                onFlipBoard={flipBoard}
                isAutoPlaying={isAutoPlaying}
                canStepBack={canStepBack}
                canStepForward={canStepForward}
                showKeyHints
              />
            </div>
          )}
        </div>
      </div>

      {/* Toast notification */}
      {toastMessage && (
        <Toast
          message={toastMessage}
          onDone={() => setToastMessage(null)}
        />
      )}
    </main>
  )
}
